import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react'
import Quill from 'quill'

const RichTextEditor = forwardRef(
  (
    { readOnly, defaultValue, onTextChange, onSelectionChange, className = '' },
    ref,
  ) => {
    const containerRef = useRef(null)
    const defaultValueRef = useRef(defaultValue)
    const onTextChangeRef = useRef(onTextChange)
    const onSelectionChangeRef = useRef(onSelectionChange)

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange
      onSelectionChangeRef.current = onSelectionChange
    })

    useEffect(() => {
      ref?.current?.enable(!readOnly)
    }, [ref, readOnly])

    useEffect(() => {
      const container = containerRef.current

      const newDiv = container.ownerDocument.createElement('div')
      const editorContainer = container.appendChild(newDiv)
      editorContainer.style.display = 'flex'
      editorContainer.style.flexDirection = 'column'
      editorContainer.style.height = '100%'
      editorContainer.style.width = '100%'
      editorContainer.className = 'h-full w-full'

      const quill = new Quill(editorContainer, {
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline'],
            ['link'],
          ],
          keyboard: {
            bindings: {
              tab: {
                key: 'Tab',
                handler: () => true,
              },
            },
          },
        },
        theme: 'snow',
      })

      // Override the link tooltip handler
      const Link = Quill.import('formats/link')
      const builtInSanitize = Link.sanitize
      Link.sanitize = function (url) {
        let sanitized = builtInSanitize.call(this, url)
        if (sanitized && !/^https?:\/\//i.test(sanitized)) {
          sanitized = 'https://' + sanitized.replace(/^\/+/, '')
        }
        return sanitized
      }

      if (ref) ref.current = quill

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current)
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args)
      })

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args)
      })

      return () => {
        if (ref) ref.current = null
        container.innerHTML = ''
      }
    }, [ref])

    return (
      <div
        className={`flex min-h-0 flex-col [&_.ql-container]:min-h-0 [&_.ql-container]:flex-1 [&_.ql-container]:overflow-hidden [&_.ql-editor]:h-full [&_.ql-editor]:min-h-[var(--rte-min-h,10rem)] [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:break-words [&_.ql-toolbar]:shrink-0 ${className}`}
        ref={containerRef}
      ></div>
    )
  },
)

RichTextEditor.displayName = 'RichTextEditor'

export default RichTextEditor
